import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, forwardRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { Backdrop, FormControl, FormControlLabel, Grid, IconButton, Radio, RadioGroup, Snackbar, Typography, useMediaQuery } from '@mui/material';
import AppLoader from '../../../components/AppLoader/AppLoader';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { tableStyleOverDue, tableStyle } from '../../../style/common';
import "../../../style/common.scss";
import "../../../style/workflowMessageBox.scss";
import "../Reconnection/ReconnectionWorkflowDialog.scss";
import "../Reconnection/ReconnectionActivityLogExtras.scss";
import "../../BillingAndCollection/MeterReadingCorrection/MeterReadingCorrectionForm.scss";
import "../../BillingAndCollection/MeterReadingCorrection/AuthorityVerification.scss";
import "./DisconnectionWorkflowDialog.scss";
import {
  WORKFLOW_MOBILE_MEDIA,
  workflowDialogClassName,
  workflowDialogShellProps,
} from '../../../utils/workflowDialogShell';
import { useDispatch, useSelector } from 'react-redux';
import ServiceDashboardTables from '../../../components/ServiceDashboardTables/ServiceDashboardTables';
import StepProgressBar from '../../../components/StepProgressBar/StepProgressBar';
import OpenBase64ImageInPopup from '../../../services/OpenBase64ImageInPopup';
import Base64Image from '../../../components/Base64Image/Base64Image';
import { SearchIndividualApplication } from '../../../network/APIs/APIs';
import { numberFormatting, subdivisionName, getDocumentBackGroundColor, convertDatewithTime, isPdf, getConsumerDetails } from '../../../services/Functions';
import { getApplicationProgressStatus } from '../../../utils/applicationProgressStatus';
import pdfIcon from '../../../images/pdfIcon.png';
import tickicon from '../../../images/Accept.png';
import crossIcon from '../../../images/remove.png';
import ProcessingSummaryTableForCoa from '../../../components/processingSummaryTable/processingSummaryTableForCoa';
import RcoActivityLogDialog, {
  RcoHistoryDoc,
  RcoHistoryField,
} from '../../../components/RcoActivityLogDialog/RcoActivityLogDialog';
import RcoActivityLogDetailDialog from '../../../components/activityLog/RcoActivityLogDetailDialog';
import { buildDcoActivityLogDetailRows } from '../../../utils/dcoActivityLogDetail';
import { ACTIVITY_LOG_TIMELINE_TYPES } from '../../../utils/activityLogDetailPdf';
import DcoRemovedMeterReadingTable from './DcoRemovedMeterReadingTable';
import { cfpBaseUrl } from '../../../network/urls';
import { portalName } from '../../../utils/PortalName';
import {
  fetchPaginatedDisconnectionNormal,
  filterDateFrom as filterDateFromNormal,
  filterDateTo as filterDateToNormal,
  filterObjectionStatus as filterObjectionStatusNormal,
  filterProgressId as filterProgressIdNormal,
  firstPage as firstPageNormal,
  lastPage as lastPageNormal,
  nextPage as nextPageNormal,
  previousPage as previousPageNormal,
  resetDateFilter as resetDateFilterNormal,
  resetObjectionStatusFilter as resetObjectionStatusFilterNormal,
  resetProgressIdFilter as resetProgressIdFilterNormal,
} from '../../../features/generalServices/dcNormalSlice';
import {
  fetchPaginatedDisconnectionOverdue,
  filterDateFrom as filterDateFromOverdue,
  filterDateTo as filterDateToOverdue,
  filterObjectionStatus as filterObjectionStatusOverdue,
  filterProgressId as filterProgressIdOverdue,
  firstPage as firstPageOverdue,
  lastPage as lastPageOverdue,
  nextPage as nextPageOverdue,
  previousPage as previousPageOverdue,
  resetDateFilter as resetDateFilterOverdue,
  resetObjectionStatusFilter as resetObjectionStatusFilterOverdue,
  resetProgressIdFilter as resetProgressIdFilterOverdue,
} from '../../../features/generalServices/dcOverdueSlice';
import { getInitialAdminOfficeCode } from '../../../utils/adminCompany';
import { DISCO_COMPANIES } from '../../../constants/discoCompanies';

export const DISCONNECTION_APPLICATION_TYPE_ID = 14;

const EMPTY_DC_STATE = {
  currentPage: 1,
  data: [],
  pageSize: 20,
  totalPages: null,
  totalRecords: null,
  dateFrom: '',
  dateTo: '',
  applicationProgressId: '',
  objectionStatus: '',
  isLoading: false,
};

const DCO_STATUS_BAR = [
  { title1: "DOCUMENT", title2: "SUBMISSION" },
  { title1: "DOCUMENT", title2: "VERIFICATION" },
  { title1: "ARREAR", title2: "VERIFICATION" },
  { title1: "REMOVE METER", title2: "READINGS" },
  { title1: "AUTHORITY", title2: "DECISION" },
  { title1: "M&T REPORT", title2: "SUBMISSION" },
  { title1: "FORWARD", title2: "TO RO" },
  { title1: "POST FOR", title2: "BILLING" },
  { title1: "COMPLETED", title2: "" },
];

const DCO_STATUS_OPTIONS = [
  "Document Submission",
  "Document Verification",
  "Arrear Verification",
  "Remove Meter Readings",
  "Authority Decision",
  "M&T Report Submission",
  "Forward To RO",
  "Post For Billing",
  "Completed",
  "Request Declined",
];

const convertDate = (dateString) => {
  if (!dateString) return '-';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear()}`;
};

const parseUnpaidDebt = (value) => {
  if (value == null || value === '') return 0;
  const numeric = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(numeric) ? numeric : 0;
};

const getUnpaidDebtFromConsumer = (consumer = {}) =>
  parseUnpaidDebt(
    consumer.UNPAID_DEBT ??
    consumer.UNPAIDDEBT ??
    consumer.unPaidDebt ??
    consumer.UNPAID_DEPTH ??
    consumer.unpaid_debt ??
    consumer.UNPAIDDEPTH,
  );

const statusLabel = (item = {}) =>
  getApplicationProgressStatus(
    item.applicationType || DISCONNECTION_APPLICATION_TYPE_ID,
    item.applicationProgressId,
    item.objectionStatus,
    item.applicationStatusId || item.remarks || 'In Process',
    'Disconnection',
  );

const getClassName = (x) => {
  if (
    x === "Pending" ||
    x === "In Process" ||
    x === "Application Submitted" ||
    x === "Document Submission"
  ) {
    return 'pending-button';
  }
  if (x === "Objection" || String(x || "").includes("Objection")) {
    return 'objection-button';
  }
  if (x === "Rejected" || x === "Request Declined") {
    return 'rejected-button';
  }
  if (x === "Approved" || x === "Completed") {
    return 'approved-button';
  }
  return 'inprocess-button';
};

const isImageData = (value) => String(value || "").slice(0, 6) === "data:i";

const initReviewFlag = (value) => {
  const n = Number(value);
  if (n === 1 || n === -1) return n;
  return null;
};

const isDocumentReviewDecided = (flag) => Number(flag) === 1 || Number(flag) === -1;

const DCO_ACTIVITY_COLUMNS = [
  { header: "Step Name", accessorKey: "stepName" },
  { header: "Processed By", accessorKey: "processedBy" },
  { header: "Date", accessorKey: "date" },
  { header: "Remarks", accessorKey: "remarks" },
];

const DcoDocCard = ({ title, front, back, flag, remarks, onOpen, children }) => {
  if (!front && !back) return null;
  const flagNum = Number(flag);
  const showTick = flagNum === 1;
  const showCross = flagNum === -1;
  const showRemarks = (showTick || showCross) && Boolean(remarks);
  const frontIsImage = isImageData(front) && !isPdf(front);
  const backIsImage = isImageData(back) && !isPdf(back);
  return (
    <Grid item lg={2}>
      <Grid
        container
        onClick={onOpen}
        sx={{
          backgroundColor: getDocumentBackGroundColor(showTick || showCross ? flag : null),
          borderRadius: "10px",
          minHeight: "150px",
          height: "auto",
          cursor: "pointer",
        }}
      >
        <Grid
          item
          lg={12}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: "5px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {front ? (
              frontIsImage
                ? <Base64Image data={front} width="80" height="50" />
                : <Base64Image data={pdfIcon} width="40" />
            ) : null}
            {back ? (
              backIsImage
                ? <Base64Image data={back} width="80" height="50" />
                : <Base64Image data={pdfIcon} width="40" />
            ) : null}
            {showTick ? (
              <img src={tickicon} width="25px" height="25px" alt="Tick Icon" className="doc-verification-status-icon" />
            ) : null}
            {showCross ? (
              <img src={crossIcon} width="25px" height="25px" alt="Cross Icon" className="doc-verification-status-icon" />
            ) : null}
            {children ? (
              <div onClick={(event) => event.stopPropagation()}>
                {children}
              </div>
            ) : null}
          </div>
        </Grid>
        <Grid item lg={12} className="tittlegrid">
          <p className="docTitle">{title}</p>
          {showRemarks ? (
            <p
              className={
                showTick
                  ? "doc-remarks-inline doc-remarks-inline--verified"
                  : "doc-remarks-inline doc-remarks-inline--rejected"
              }
            >
              {remarks}
            </p>
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  );
};

const fetchDcoDocuments = async (token, applicationId) => {
  try {
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);
    myHeaders.append("Content-Type", "application/json");
    const response = await fetch(cfpBaseUrl + "dco/getDCODocuments", {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify({ applicationId }),
      redirect: "follow",
    });
    const result = await response.json();
    if (String(result?.status) === "200") {
      const rows = Array.isArray(result?.data) ? result.data : [];
      return rows[0] || {};
    }
  } catch {
    // ignore
  }
  return {};
};

const fetchDcoHistoryPayload = async (token, applicationId) => {
  try {
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);
    myHeaders.append("Content-Type", "application/json");
    const response = await fetch(cfpBaseUrl + "dco/getBasicDCOHistory", {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify({ applicationId }),
      redirect: "follow",
    });
    const result = await response.json();
    if (String(result?.status) !== "200" || !result?.data) {
      return { history: [], currentStatus: {} };
    }
    const history = Array.isArray(result.data?.history)
      ? result.data.history
      : Array.isArray(result.data)
        ? result.data
        : [];
    const currentStatus = Array.isArray(result.data?.currentStatus)
      ? (result.data.currentStatus[0] || {})
      : (result.data?.currentStatus || {});
    return { history: history.filter(Boolean), currentStatus };
  } catch {
    return { history: [], currentStatus: {} };
  }
};

const fetchDcoIbsMeterProfile = async (token, customerId) => {
  const searchById = String(customerId || "").replace(/\s/g, "");
  if (!searchById) return [];
  try {
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);
    myHeaders.append("Content-Type", "application/json");
    const response = await fetch(cfpBaseUrl + "meterChangeOrder/getIBSMeterProfile", {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify({ customerId: searchById }),
      redirect: "follow",
    });
    const result = await response.json();
    const ok =
      result?.status === 200 ||
      result?.status === "200" ||
      response.ok;
    const profileData = Array.isArray(result?.data) ? result.data : [];
    if (ok && profileData.length > 0) return profileData;
  } catch {
    // ignore
  }
  return [];
};

const fetchDcoRemovedMeterReadings = async (token, applicationId) => {
  if (!applicationId) return [];
  try {
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);
    myHeaders.append("Content-Type", "application/json");
    const response = await fetch(cfpBaseUrl + "dco/getDCORemovedMeterReadings", {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify({ applicationId }),
      redirect: "follow",
    });
    const result = await response.json();
    const ok = result?.status === 200 || result?.status === "200";
    const rows = Array.isArray(result?.data) ? result.data : [];
    if (ok && rows.length > 0) return rows;
  } catch {
    // ignore
  }
  return [];
};

const attachDcoFeeder = (rows, application = {}) => {
  const list = Array.isArray(rows) ? rows : [];
  const feeder = application.feederCode || application.feederNo || application.FEEDER_NO || "";
  if (!feeder) return list;
  return list.map((row) => ({
    ...row,
    feederNo: row.feederNo || row.FEEDER_NO || feeder,
    FEEDER_NO: row.FEEDER_NO || row.feederNo || feeder,
  }));
};

const normalizeDcoHistoryItem = (item = {}, application = {}) => ({
  ...item,
  dcoProgStatusName:
    item.dcoProgStatusName ||
    item.disConProgStatusName ||
    item.reConProgStatusName ||
    item.stepName ||
    statusLabel(item.applicationProgressId != null ? item : application),
});

const mapHistoryToActivityRows = (history, application = {}) =>
  (Array.isArray(history) ? history : []).map((item) => ({
    stepName:
      item.dcoProgStatusName ||
      item.disConProgStatusName ||
      item.reConProgStatusName ||
      item.stepName ||
      statusLabel(item),
    processedBy: item.lastModifiedBy || item.createdBy || application.createdBy || "-",
    date: item.lastModificationDate || item.creationDate
      ? convertDatewithTime(item.lastModificationDate || item.creationDate)
      : "-",
    remarks: item.remarks || item.remarksDetail || "-",
    applicationProgressId: item.applicationProgressId,
    _raw: item,
  }));

const fallbackActivityRows = (application = {}) => {
  if (!application?.applicationId) return [];
  return [{
    stepName: statusLabel(application),
    processedBy: application.lastModifiedBy || application.createdBy || "-",
    date: application.lastModificationDate || application.creationDate
      ? convertDatewithTime(application.lastModificationDate || application.creationDate)
      : "-",
    remarks: application.remarks || application.remarksDetail || "Application Submitted",
    applicationProgressId: application.applicationProgressId,
    _raw: application,
  }];
};

const DcoDetailItem = ({ title, value }) => (
  <div className="first-row-item">
    <p className="prop-title">{title}</p>
    <p className="prop-detail">{value || "-"}</p>
  </div>
);

const DCO_TYPE_LABELS = {
  7: "Temporary",
  8: "Permanent",
};

const disconnectionTypeLabel = (item = {}) => {
  if (item.disconnectionType) return item.disconnectionType;
  const typeId = item.DCOType ?? item.dcoType;
  if (typeId === 7 || typeId === "7") return "Temporary";
  if (typeId === 8 || typeId === "8") return "Permanent";
  if (typeId == null || typeId === "" || typeId === 0 || typeId === "0") return "-";
  return DCO_TYPE_LABELS[typeId] || String(typeId);
};

const mapRow = (item) => ({
  applicationId: item.applicationId,
  creationDate: convertDate(item.creationDate),
  referenceNo: item.referenceNo || item.refNo || "-",
  name: item.applicantName || item.consumerName || item.name || "-",
  disconnectionType: disconnectionTypeLabel(item),
  applicationStatusId: statusLabel(item),
  applicationProgressId: item.applicationProgressId,
  releventApplicationFlage: item.releventApplicationFlage,
});

const mapRows = (rows) =>
  (Array.isArray(rows) ? rows : []).filter((item) => item?.applicationId).map(mapRow);

const DC_MOBILE_FIELDS = ["applicationId", "name", "applicationProgressId"];

const DisconnectionTable = forwardRef((props, ref) => {
  const { token, code: officeCode, selectedAdminCompanyCode } = useSelector((state) => state.auth);
  const isWorkflowMobile = useMediaQuery(WORKFLOW_MOBILE_MEDIA);
  const [selectedOfficeCode, setSelectedOfficeCode] = useState(
    getInitialAdminOfficeCode(officeCode, selectedAdminCompanyCode),
  );
  const apiOfficeCode = String(officeCode) === "10"
    ? selectedOfficeCode
    : (selectedOfficeCode || officeCode);

  const [error, setError] = useState();
  const [open, setOpen] = useState(false);
  const [dialogRowData, setDialogRowData] = useState({});
  const [viewBackdrop, setViewBackdrop] = useState(false);
  const [copyOfApplicantCNICVerifyPopUp, setCopyOfApplicantCNICVerifyPopUp] = useState(false);
  const [paidBillImagePopUp, setPaidBillImagePopUp] = useState(false);
  const [dcoNoticePopUp, setDcoNoticePopUp] = useState(false);
  const [dcoDocuments, setDcoDocuments] = useState({});
  const [flag_applicantCNICFront, setFlag_applicantCNICFront] = useState(null);
  const [flag_applicantCNICBack, setFlag_applicantCNICBack] = useState(null);
  const [flag_paidBillImage, setFlag_paidBillImage] = useState(null);
  const [flag_dcoNotice, setFlag_dcoNotice] = useState(null);
  const [applicantCNICFrontRemarks, setApplicantCNICFrontRemarks] = useState("");
  const [applicantCNICBackRemarks, setApplicantCNICBackRemarks] = useState("");
  const [paidBillImageRemarks, setPaidBillImageRemarks] = useState("");
  const [dcoNoticeRemarks, setDcoNoticeRemarks] = useState("");
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityLoaded, setActivityLoaded] = useState(false);
  const [activityLogCollapsed, setActivityLogCollapsed] = useState(true);
  const [activityLogDetailOpen, setActivityLogDetailOpen] = useState(false);
  const [historyDetailPopUp, setHistoryDetailPopUp] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [dcoHistoryItems, setDcoHistoryItems] = useState([]);
  const [dcoHistoryCurrent, setDcoHistoryCurrent] = useState({});
  const [paymentStatus, setPaymentStatus] = useState("");
  const [arrearAmount, setArrearAmount] = useState("");
  const [removedMeterProfile, setRemovedMeterProfile] = useState([]);
  const [authorityMeterReadings, setAuthorityMeterReadings] = useState([]);
  const [referToFieldSurvey, setReferToFieldSurvey] = useState("");
  const removedMeterTableRef = useRef(null);
  const subDivisions = useMemo(() => {
    try {
      return JSON.parse(window.localStorage.getItem("subDivisions") || "[]");
    } catch {
      return [];
    }
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const foundId = location.state?.foundId;
  const openedFoundIdRef = useRef(null);
  const currentPath = location.pathname;
  const dispatch = useDispatch();
  const showDashboard = Boolean(props.showTable) && currentPath === "/disconnection-table";

  const dcNormal = useSelector((state) => state.dcNormalPaginated) || EMPTY_DC_STATE;
  const dcOverdue = useSelector((state) => state.dcOverduePaginated) || EMPTY_DC_STATE;

  const {
    currentPage: currentPageNormal,
    data: dataNormal,
    pageSize: pageSizeNormal,
    totalPages: totalPagesNormal,
    totalRecords: totalRecordsNormal,
    dateFrom: dateFromNormal,
    dateTo: dateToNormal,
    applicationProgressId: applicationProgressIdNormal,
    objectionStatus: objectionStatusNormal,
    isLoading: isLoad,
  } = dcNormal;
  const {
    currentPage: currentPageOverdue,
    data: dataOverdue,
    pageSize: pageSizeOverdue,
    totalPages: totalPagesOverdue,
    totalRecords: totalRecordsOverdue,
    dateFrom: dateFromOverdue,
    dateTo: dateToOverdue,
    applicationProgressId: applicationProgressIdOverdue,
    objectionStatus: objectionStatusOverdue,
    isLoading: isLoadd,
  } = dcOverdue;

  const applicationsNormalToDataGrid = useMemo(() => mapRows(dataNormal), [dataNormal]);
  const applicationsOverdueToDataGrid = useMemo(() => mapRows(dataOverdue), [dataOverdue]);

  useEffect(() => {
    if (String(officeCode) === "10" && selectedAdminCompanyCode) {
      const nextCode = String(selectedAdminCompanyCode);
      setSelectedOfficeCode((prev) => (prev === nextCode ? prev : nextCode));
    }
  }, [officeCode, selectedAdminCompanyCode]);

  const handleRowClick = useCallback(async (applicationId, extra) => {
    const extraRow = extra && typeof extra === "object" && (extra.applicationId || extra.eOpsTrackingId)
      ? extra
      : {};
    const resolvedId = applicationId || extraRow.applicationId || extraRow.eOpsTrackingId;
    const totalArray = (Array.isArray(dataNormal) ? dataNormal : [])
      .concat(Array.isArray(dataOverdue) ? dataOverdue : []);
    let itemFound = totalArray.find((item) => item.applicationId === resolvedId) || extraRow;
    setActivityLog([]);
    setActivityLoaded(false);
    setActivityLogCollapsed(true);
    setActivityLogDetailOpen(false);
    setHistoryDetailPopUp(false);
    setHistoryData(null);
    setDcoHistoryItems([]);
    setDcoHistoryCurrent({});
    setPaymentStatus("");
    setArrearAmount("");
    setRemovedMeterProfile([]);
    setAuthorityMeterReadings([]);
    setReferToFieldSurvey("");
    setCopyOfApplicantCNICVerifyPopUp(false);
    setPaidBillImagePopUp(false);
    setDcoNoticePopUp(false);
    setFlag_applicantCNICFront(null);
    setFlag_applicantCNICBack(null);
    setFlag_paidBillImage(null);
    setFlag_dcoNotice(null);
    setApplicantCNICFrontRemarks("");
    setApplicantCNICBackRemarks("");
    setPaidBillImageRemarks("");
    setDcoNoticeRemarks("");
    setViewBackdrop(true);
    try {
      const typeCandidates = Array.from(new Set([
        String(
          itemFound.applicationType
          || itemFound.applicationTypeId
          || extraRow.applicationTypeId
          || extraRow.applicationType
          || DISCONNECTION_APPLICATION_TYPE_ID
        ),
        "14",
        "20",
      ].filter(Boolean)));
      const docsPromise = fetchDcoDocuments(token, resolvedId).catch(() => ({}));
      const historyPromise = fetchDcoHistoryPayload(token, resolvedId).catch(() => ({ history: [], currentStatus: {} }));
      const meterPromise = fetchDcoIbsMeterProfile(
        token,
        itemFound.customerId || extraRow.customerId,
      ).catch(() => []);
      const progressGuess = Number(
        itemFound.applicationProgressId ?? extraRow.applicationProgressId,
      );
      const submittedReadingsPromise = progressGuess === 4
        ? fetchDcoRemovedMeterReadings(token, resolvedId).catch(() => [])
        : Promise.resolve([]);
      let detail = null;
      for (const typeId of typeCandidates) {
        detail = await SearchIndividualApplication(token, resolvedId, typeId);
        if (detail) break;
      }
      const [docs, historyPayload, meterProfile, submittedReadingsRaw] = await Promise.all([
        docsPromise,
        historyPromise,
        meterPromise,
        submittedReadingsPromise,
      ]);
      const merged = {
        ...itemFound,
        ...(detail || {}),
        applicationId: resolvedId || itemFound.applicationId || detail?.applicationId,
      };
      if (!merged.applicationId) return;
      const nextDocs = docs || {};
      setDialogRowData(merged);
      setDcoDocuments(nextDocs);
      const cnicFrontFlag = initReviewFlag(nextDocs.applicantCNICFrontFlag);
      const cnicBackFlag = initReviewFlag(nextDocs.applicantCNICBackFlag);
      const cnicNeedsReview = cnicFrontFlag == null || cnicBackFlag == null;
      setFlag_applicantCNICFront(cnicNeedsReview ? null : cnicFrontFlag);
      setFlag_applicantCNICBack(cnicNeedsReview ? null : cnicBackFlag);
      setFlag_paidBillImage(initReviewFlag(nextDocs.paidBillImageFlag));
      setFlag_dcoNotice(initReviewFlag(nextDocs.dcoNoticeDocFlag ?? nextDocs.dcoNoticeImageFlag));
      setApplicantCNICFrontRemarks(nextDocs.applicantCNICFrontRemarks || "");
      setApplicantCNICBackRemarks(nextDocs.applicantCNICBackRemarks || "");
      setPaidBillImageRemarks(nextDocs.paidBillImageRemarks || "");
      setDcoNoticeRemarks(nextDocs.dcoNoticeDocRemarks || nextDocs.dcoNoticeImageRemarks || "");
      const sortedHistory = [...(historyPayload?.history || [])]
        .map((item) => normalizeDcoHistoryItem(item, merged))
        .sort((a, b) => {
          const idDiff = Number(b.historyId || 0) - Number(a.historyId || 0);
          if (idDiff !== 0) return idDiff;
          return new Date(b.lastModificationDate || 0) - new Date(a.lastModificationDate || 0);
        });
      const currentHistory = normalizeDcoHistoryItem(historyPayload?.currentStatus || {}, merged);
      setDcoHistoryItems(sortedHistory);
      setDcoHistoryCurrent({
        ...currentHistory,
        applicationProgressId: merged.applicationProgressId ?? currentHistory.applicationProgressId,
      });
      const mappedHistory = mapHistoryToActivityRows(sortedHistory, merged);
      setActivityLog(mappedHistory.length ? mappedHistory : fallbackActivityRows(merged));
      setActivityLoaded(true);
      let meters = Array.isArray(meterProfile) ? meterProfile : [];
      const customerId = String(merged.customerId || "").replace(/\s/g, "");
      const startedWithId = String(itemFound.customerId || extraRow.customerId || "").replace(/\s/g, "");
      if (!meters.length && customerId && customerId !== startedWithId) {
        meters = await fetchDcoIbsMeterProfile(token, customerId);
      }
      setRemovedMeterProfile(meters);
      const mergedProgress = Number(merged.applicationProgressId);
      let submittedReadings = Array.isArray(submittedReadingsRaw) ? submittedReadingsRaw : [];
      if (mergedProgress === 4 && submittedReadings.length === 0) {
        submittedReadings = await fetchDcoRemovedMeterReadings(token, resolvedId);
      }
      setAuthorityMeterReadings(mergedProgress === 4 ? submittedReadings : []);
      setReferToFieldSurvey("");
      setOpen(true);
    } finally {
      setViewBackdrop(false);
    }
  }, [dataNormal, dataOverdue, token]);

  const loadActivityLog = useCallback(async () => {
    const applicationId = dialogRowData.applicationId;
    if (!applicationId || activityLoading) return;
    setActivityLoading(true);
    try {
      const payload = await fetchDcoHistoryPayload(token, applicationId);
      const sortedHistory = [...(payload.history || [])]
        .map((item) => normalizeDcoHistoryItem(item, dialogRowData))
        .sort((a, b) => {
          const idDiff = Number(b.historyId || 0) - Number(a.historyId || 0);
          if (idDiff !== 0) return idDiff;
          return new Date(b.lastModificationDate || 0) - new Date(a.lastModificationDate || 0);
        });
      const currentHistory = normalizeDcoHistoryItem(payload.currentStatus || {}, dialogRowData);
      setDcoHistoryItems(sortedHistory);
      setDcoHistoryCurrent({
        ...currentHistory,
        applicationProgressId: dialogRowData.applicationProgressId ?? currentHistory.applicationProgressId,
      });
      const mappedHistory = mapHistoryToActivityRows(sortedHistory, dialogRowData);
      setActivityLog(mappedHistory.length ? mappedHistory : fallbackActivityRows(dialogRowData));
      setActivityLoaded(true);
    } finally {
      setActivityLoading(false);
    }
  }, [activityLoading, dialogRowData, token]);

  const openActivityLogDetails = (rowOrStep) => {
    const historyRow = rowOrStep && typeof rowOrStep === "object"
      ? (rowOrStep._raw || rowOrStep)
      : null;
    if (!historyRow) return;
    setHistoryData(normalizeDcoHistoryItem(historyRow, dialogRowData));
    setHistoryDetailPopUp(true);
  };

  const refreshDisconnectionTables = () => {
    if (!token) return;
    dispatch(fetchPaginatedDisconnectionNormal({
      page: currentPageNormal,
      pageSize: pageSizeNormal,
      dateFrom: dateFromNormal,
      dateTo: dateToNormal,
      loadFrom: '',
      loadTo: '',
      applicationProgressId: applicationProgressIdNormal,
      objectionStatus: objectionStatusNormal,
      officeCode: apiOfficeCode,
    }));
    dispatch(fetchPaginatedDisconnectionOverdue({
      page: currentPageOverdue,
      pageSize: pageSizeOverdue,
      dateFrom: dateFromOverdue,
      dateTo: dateToOverdue,
      applicationProgressId: applicationProgressIdOverdue,
      objectionStatus: objectionStatusOverdue,
      officeCode: apiOfficeCode,
    }));
  };

  const handleClose = () => {
    setOpen(false);
    setDialogRowData({});
    setCopyOfApplicantCNICVerifyPopUp(false);
    setPaidBillImagePopUp(false);
    setDcoNoticePopUp(false);
    setDcoDocuments({});
    setFlag_applicantCNICFront(null);
    setFlag_applicantCNICBack(null);
    setFlag_paidBillImage(null);
    setFlag_dcoNotice(null);
    setApplicantCNICFrontRemarks("");
    setApplicantCNICBackRemarks("");
    setPaidBillImageRemarks("");
    setDcoNoticeRemarks("");
    setActivityLog([]);
    setActivityLoaded(false);
    setActivityLogCollapsed(true);
    setActivityLogDetailOpen(false);
    setHistoryDetailPopUp(false);
    setHistoryData(null);
    setDcoHistoryItems([]);
    setDcoHistoryCurrent({});
    setPaymentStatus("");
    setArrearAmount("");
    setRemovedMeterProfile([]);
    setAuthorityMeterReadings([]);
    setReferToFieldSurvey("");
    setError('');
    setViewBackdrop(false);
    refreshDisconnectionTables();
  };

  const progressId = Number(dialogRowData.applicationProgressId);
  const objectionStatus = Number(dialogRowData.objectionStatus || 0);
  const areaTypeLabel =
    dialogRowData.areaType === "U"
      ? "Urban"
      : dialogRowData.areaType === "R"
        ? "Rural"
        : (dialogRowData.areaType || "-");
  const hasCnic = Boolean(dcoDocuments.applicantCNICFront || dcoDocuments.applicantCNICBack);
  const hasPaidBill = Boolean(dcoDocuments.paidBillImage);
  const hasNotice = Boolean(dcoDocuments.dcoNoticeDoc || dcoDocuments.dcoNoticeImage);
  const hasDocuments = hasCnic || hasPaidBill || hasNotice;
  const canReviewDocuments = progressId === 1 && objectionStatus !== 1;
  const canReviewArrears = progressId === 2;
  const canReviewRemovedMeter = progressId === 3;
  const canReviewAuthorityDecision = progressId === 4;
  const removedMeterUnits = useMemo(() => {
    const raw =
      (Array.isArray(removedMeterProfile) && removedMeterProfile.length > 0
        ? removedMeterProfile
        : null)
      || dialogRowData.removedMeterUnits
      || dialogRowData.meterUnits
      || dialogRowData.removeMeterInfo
      || dialogRowData.meterProfile
      || [];
    const rows = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.rows)
          ? raw.rows
          : [];
    return attachDcoFeeder(rows, dialogRowData);
  }, [dialogRowData, removedMeterProfile]);
  const authorityMeterUnits = useMemo(
    () => attachDcoFeeder(authorityMeterReadings, dialogRowData),
    [authorityMeterReadings, dialogRowData],
  );
  const canSubmitDcoDocumentVerification =
    (!hasCnic || (isDocumentReviewDecided(flag_applicantCNICFront) && isDocumentReviewDecided(flag_applicantCNICBack))) &&
    (!hasPaidBill || isDocumentReviewDecided(flag_paidBillImage));
  const hasRejectedDcoDocument =
    Number(flag_applicantCNICFront) === -1 ||
    Number(flag_applicantCNICBack) === -1 ||
    Number(flag_paidBillImage) === -1;
  const hasRejectedDocsToReupload =
    Number(dcoDocuments.applicantCNICFrontFlag) === -1 ||
    Number(dcoDocuments.applicantCNICBackFlag) === -1 ||
    Number(dcoDocuments.paidBillImageFlag) === -1;

  const updateDocFlag = (documentName, value, remarks) => {
    switch (documentName) {
      case "copyOfApplicantCNIC":
        setFlag_applicantCNICFront(value);
        setFlag_applicantCNICBack(value);
        setApplicantCNICFrontRemarks(remarks);
        setApplicantCNICBackRemarks(remarks);
        setCopyOfApplicantCNICVerifyPopUp(false);
        break;
      case "paidBillImage":
        setFlag_paidBillImage(value);
        setPaidBillImageRemarks(remarks);
        setPaidBillImagePopUp(false);
        break;
      case "dcoNotice":
        setFlag_dcoNotice(value);
        setDcoNoticeRemarks(remarks);
        setDcoNoticePopUp(false);
        break;
      default:
        break;
    }
  };

  const callingUpdateDocumentStatusAPI = async () => {
    const isReject = hasRejectedDcoDocument;
    setViewBackdrop(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      myHeaders.append("Content-Type", "application/json");
      const payload = {
        applicationId: dialogRowData.applicationId,
        applicationProgressId: isReject ? 1 : 2,
        objectionStatus: isReject ? 1 : 0,
        remarks: isReject ? "Documents Rejected." : "Documents Verified.",
        applicantCNICFrontFlag: String(hasCnic ? flag_applicantCNICFront : 1),
        applicantCNICBackFlag: String(hasCnic ? flag_applicantCNICBack : 1),
        paidBillImageFlag: String(hasPaidBill ? flag_paidBillImage : 1),
        applicantCNICFrontRemarks: hasCnic ? (applicantCNICFrontRemarks || (isReject ? "Rejected" : "Verified")) : "Verified",
        applicantCNICBackRemarks: hasCnic ? (applicantCNICBackRemarks || (isReject ? "Rejected" : "Verified")) : "Verified",
        paidBillImageRemarks: hasPaidBill ? (paidBillImageRemarks || (isReject ? "Rejected" : "Verified")) : "Verified",
      };
      const response = await fetch(cfpBaseUrl + "dco/verifyDCODocuments", {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify(payload),
        redirect: "follow",
      });
      const result = await response.json();
      if (String(result?.status) === "200") {
        handleClose();
      } else {
        setError(result?.message || "Failed to update document status.");
      }
    } catch {
      setError("Failed to update document status.");
    } finally {
      setViewBackdrop(false);
    }
  };

  const updateDocumentStatus = () => {
    if (!canSubmitDcoDocumentVerification) {
      setError("Accept or reject each document before verifying.");
      return;
    }
    callingUpdateDocumentStatusAPI();
  };

  const checkDcoArrears = async (row = dialogRowData) => {
    const searchById = String(row?.customerId || row?.referenceNo || row?.refNo || "").replace(/\s/g, "");
    if (!searchById) {
      setError("Customer ID not found for arrear check.");
      return;
    }
    setViewBackdrop(true);
    try {
      const apiResponse = await getConsumerDetails(token, searchById);
      const consumer = Array.isArray(apiResponse?.data) ? apiResponse.data[0] : null;
      const okStatus = apiResponse?.status === "200" || apiResponse?.status === 200;
      if (!okStatus || !consumer) {
        setError(apiResponse?.message || "Failed to check arrears.");
        setPaymentStatus("");
        setArrearAmount("");
        return;
      }
      const unpaidDebt = getUnpaidDebtFromConsumer(consumer);
      setArrearAmount(unpaidDebt);
      setPaymentStatus(unpaidDebt > 0 ? "Arrear exists" : "No arrears exist");
      setError("");
    } catch {
      setError("Failed to check arrears.");
      setPaymentStatus("");
      setArrearAmount("");
    } finally {
      setViewBackdrop(false);
    }
  };

  const callArrearVerificationAPI = async (
    applicationId,
    applicationProgresId,
    objectionStatuss,
    remarks,
    remarksDetails,
  ) => {
    if (!applicationId) return;
    setViewBackdrop(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      myHeaders.append("Content-Type", "application/json");
      const raw = JSON.stringify({
        applicationId,
        applicationProgressId: applicationProgresId,
        objectionStatus: objectionStatuss,
        remarks,
        remarksDetail: remarksDetails,
      });
      const response = await fetch(cfpBaseUrl + "dco/processDCOArrearsVerification", {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      });
      const result = await response.json();
      if (String(result?.status) === "200") {
        handleClose();
      } else {
        setError(result?.message || "Failed to verify arrears.");
      }
    } catch {
      setError("Failed to verify arrears.");
    } finally {
      setViewBackdrop(false);
    }
  };

  const submitRemovedMeterReadings = async () => {
    const applicationId = dialogRowData.applicationId;
    if (!applicationId) return;
    const result = removedMeterTableRef.current?.validate?.();
    if (!result?.ok) {
      setError(
        result?.message
        || "Enter Removed Meter Reading and upload Evidence for every KWH/KVARH meter.",
      );
      return;
    }
    const referenceNo = String(
      dialogRowData.referenceNo || dialogRowData.refNo || dialogRowData.ckey || "",
    ).replace(/\s/g, "");
    const emptyIfMissing = (value) => (value == null || value === "" ? "" : value);
    const meterData = (result.rows || []).map((row) => {
      const units = row.isMdi || row.unitsConsumed == null ? "" : row.unitsConsumed;
      return {
        referenceNo,
        removeMeterId: emptyIfMissing(row.meterId),
        removeSetNo: emptyIfMissing(row.setNo),
        removeMeterSerial: emptyIfMissing(row.serialNo),
        removedAdvanceUnits: emptyIfMissing(units),
        removeCons: units === "" ? "" : String(units),
        removeRoundComCode: emptyIfMissing(row.roundingCompCode),
        removeMeterMasterReading: emptyIfMissing(row.masterReading),
        removeMeterReading: row.isMdi ? "" : emptyIfMissing(row.removedMeterReading),
        removedMultiplyingFactor: emptyIfMissing(row.mf),
        removeMeterImages: row.isMdi ? "" : emptyIfMissing(row.evidence),
      };
    });
    setViewBackdrop(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      myHeaders.append("Content-Type", "application/json");
      const raw = JSON.stringify({
        applicationId,
        applicationProgressId: progressId + 1,
        objectionStatus: 0,
        remarks: "Removed Meter Readings Submitted.",
        readingsSubmittedThrough: portalName || "",
        meterData,
      });
      const response = await fetch(cfpBaseUrl + "dco/removedMeterReadings", {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      });
      const apiResult = await response.json();
      if (String(apiResult?.status) === "200") {
        handleClose();
      } else {
        setError(apiResult?.message || "Failed to submit removed meter readings.");
      }
    } catch {
      setError("Failed to submit removed meter readings.");
    } finally {
      setViewBackdrop(false);
    }
  };

  const handleNextPage = () => dispatch(nextPageNormal());
  const handlePreviousPage = () => dispatch(previousPageNormal());
  const handleFirstPage = () => dispatch(firstPageNormal());
  const handleLastPage = (page) =>
    dispatch(lastPageNormal(typeof page === 'number' ? page : totalPagesNormal));
  const handleDateFromFilter = (value) => { if (value) dispatch(filterDateFromNormal(value)); };
  const handleDateToFilter = (value) => { if (value) dispatch(filterDateToNormal(value)); };
  const handleResetDateFilter = () => dispatch(resetDateFilterNormal());
  const handleProgressIdFilter = (value) => {
    if (value || value === 0) dispatch(filterProgressIdNormal(value));
  };
  const handleResetProgressIdFilter = () => dispatch(resetProgressIdFilterNormal());
  const handleObjectionStatusFilter = (value) => {
    if (value || value === 0) dispatch(filterObjectionStatusNormal(value));
  };
  const handleResetObjectionStatusFilter = () => dispatch(resetObjectionStatusFilterNormal());

  const handleNextPageOverdue = () => dispatch(nextPageOverdue());
  const handlePreviousPageOverdue = () => dispatch(previousPageOverdue());
  const handleFirstPageOverdue = () => dispatch(firstPageOverdue());
  const handleLastPageOverdue = (page) =>
    dispatch(lastPageOverdue(typeof page === 'number' ? page : totalPagesOverdue));
  const handleDateFromFilterOverdue = (value) => { if (value) dispatch(filterDateFromOverdue(value)); };
  const handleDateToFilterOverdue = (value) => { if (value) dispatch(filterDateToOverdue(value)); };
  const handleResetDateFilterOverdue = () => dispatch(resetDateFilterOverdue());
  const handleProgressIdFilterOverdue = (value) => {
    if (value || value === 0) dispatch(filterProgressIdOverdue(value));
  };
  const handleResetProgressIdFilterOverdue = () => dispatch(resetProgressIdFilterOverdue());
  const handleObjectionStatusFilterOverdue = (value) => {
    if (value || value === 0) dispatch(filterObjectionStatusOverdue(value));
  };
  const handleResetObjectionStatusFilterOverdue = () => dispatch(resetObjectionStatusFilterOverdue());

  useEffect(() => {
    if (!showDashboard || !token) return;
    dispatch(fetchPaginatedDisconnectionNormal({
      page: currentPageNormal,
      pageSize: pageSizeNormal,
      dateFrom: dateFromNormal,
      dateTo: dateToNormal,
      loadFrom: '',
      loadTo: '',
      applicationProgressId: applicationProgressIdNormal,
      objectionStatus: objectionStatusNormal,
      officeCode: apiOfficeCode,
    }));
  }, [
    showDashboard,
    currentPageNormal,
    pageSizeNormal,
    dateFromNormal,
    dateToNormal,
    applicationProgressIdNormal,
    objectionStatusNormal,
    apiOfficeCode,
    token,
    dispatch,
  ]);

  useEffect(() => {
    if (!showDashboard || !token) return;
    dispatch(fetchPaginatedDisconnectionOverdue({
      page: currentPageOverdue,
      pageSize: pageSizeOverdue,
      dateFrom: dateFromOverdue,
      dateTo: dateToOverdue,
      applicationProgressId: applicationProgressIdOverdue,
      objectionStatus: objectionStatusOverdue,
      officeCode: apiOfficeCode,
    }));
  }, [
    showDashboard,
    currentPageOverdue,
    pageSizeOverdue,
    dateFromOverdue,
    dateToOverdue,
    applicationProgressIdOverdue,
    objectionStatusOverdue,
    apiOfficeCode,
    token,
    dispatch,
  ]);

  useEffect(() => {
    if (!showDashboard) return;
    if (!foundId || openedFoundIdRef.current === foundId) return;
    openedFoundIdRef.current = foundId;
    handleRowClick(foundId);
  }, [showDashboard, foundId, handleRowClick]);

  useEffect(() => {
    if (!location.state?.refreshDisconnectionList) return;
    refreshDisconnectionTables();
    navigate(currentPath, { replace: true, state: {} });
  }, [location.state?.refreshDisconnectionList]);

  const tblColumns = useMemo(() => [
    {
      field: "applicationId",
      headerName: 'Application No.',
      align: 'center',
      sortable: false,
      filterable: false,
      showFullApplicationId: true,
      preserveColumnWidth: true,
      headerStyle: {
        ...tableStyle.headerStyle,
        width: '22%',
        minWidth: '210px',
        maxWidth: 'none',
      },
      headerCellStyle: tableStyle.headerCellStyle,
      rowCellStyle: {
        ...tableStyle.rowCellStyle,
        minWidth: '210px',
        maxWidth: 'none',
        overflow: 'visible',
        textOverflow: 'clip',
      },
    },
    {
      field: "creationDate",
      type: 'dateTime',
      headerName: 'Submission Date',
      align: 'center',
      sortable: false,
      filterable: false,
      preserveColumnWidth: true,
      headerStyle: {
        ...tableStyle.headerStyle,
        width: '11%',
        minWidth: '88px',
        maxWidth: '108px',
      },
      headerCellStyle: tableStyle.headerCellStyle,
      rowCellStyle: {
        ...tableStyle.rowCellStyle,
        minWidth: '88px',
        maxWidth: '108px',
      },
    },
    {
      field: "referenceNo",
      headerName: 'Reference No.',
      align: 'center',
      sortable: false,
      filterable: false,
      preserveColumnWidth: true,
      headerStyle: {
        ...tableStyle.headerStyle,
        width: '13%',
        minWidth: '100px',
        maxWidth: '128px',
      },
      headerCellStyle: tableStyle.headerCellStyle,
      rowCellStyle: {
        ...tableStyle.rowCellStyle,
        minWidth: '100px',
        maxWidth: '128px',
      },
    },
    {
      field: "name",
      headerName: 'Applicant Name',
      align: 'left',
      sortable: false,
      filterable: false,
      headerStyle: tableStyle.headerStyle,
      headerCellStyle: tableStyle.headerCellStyle,
      rowCellStyle: tableStyle.rowCellStyle,
    },
    {
      field: "disconnectionType",
      headerName: 'Type',
      align: 'center',
      sortable: false,
      filterable: false,
      preserveColumnWidth: true,
      headerStyle: {
        ...tableStyle.headerStyle,
        width: '8%',
        minWidth: '70px',
        maxWidth: '88px',
      },
      headerCellStyle: tableStyle.headerCellStyle,
      rowCellStyle: {
        ...tableStyle.rowCellStyle,
        minWidth: '70px',
        maxWidth: '88px',
      },
    },
    {
      field: "applicationProgressId",
      headerStyle: {
        ...tableStyle.headerStyle,
        width: '20%',
        minWidth: '180px',
        maxWidth: 'none',
      },
      headerCellStyle: tableStyle.headerCellStyle,
      rowCellStyle: {
        ...tableStyle.rowCellStyle,
        minWidth: '180px',
        maxWidth: 'none',
        overflow: 'visible',
        textOverflow: 'clip',
        whiteSpace: 'nowrap',
      },
      headerName: "Status",
      align: 'center',
      sortable: false,
      filterable: true,
      type: 'singleSelect',
      showFullText: true,
      preserveColumnWidth: true,
      valueOptions: DCO_STATUS_OPTIONS,
      renderCell: (params) => {
        const btnText = params.applicationStatusId;
        return <div className={getClassName(btnText)}>{btnText}</div>;
      }
    },
  ], []);

  const tblColumnsOverDue = useMemo(() => tblColumns.map((col) => ({
    ...col,
    headerStyle: {
      ...tableStyleOverDue.headerStyle,
      width: col.headerStyle?.width,
      minWidth: col.headerStyle?.minWidth,
      maxWidth: col.headerStyle?.maxWidth,
    },
    headerCellStyle: tableStyleOverDue.headerCellStyle,
    rowCellStyle: {
      ...tableStyleOverDue.rowCellStyle,
      minWidth: col.rowCellStyle?.minWidth,
      maxWidth: col.rowCellStyle?.maxWidth,
      overflow: col.rowCellStyle?.overflow,
      textOverflow: col.rowCellStyle?.textOverflow,
      whiteSpace: col.rowCellStyle?.whiteSpace,
    },
  })), [tblColumns]);

  useImperativeHandle(ref, () => ({
    handleRowClick
  }), [handleRowClick]);

  const documentsPanel = (
    <Grid
      className="document-verification-container"
      container
      spacing={1}
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        marginTop: "1%",
        padding: "2%",
      }}
    >
      <DcoDocCard
        title="Applicant's CNIC."
        front={dcoDocuments.applicantCNICFront}
        back={dcoDocuments.applicantCNICBack}
        flag={flag_applicantCNICFront ?? dcoDocuments.applicantCNICFrontFlag ?? dcoDocuments.applicantCNICBackFlag}
        remarks={applicantCNICBackRemarks || applicantCNICFrontRemarks || dcoDocuments.applicantCNICBackRemarks || dcoDocuments.applicantCNICFrontRemarks}
        onOpen={() => {
          if (!copyOfApplicantCNICVerifyPopUp) setCopyOfApplicantCNICVerifyPopUp(true);
        }}
      >
        {copyOfApplicantCNICVerifyPopUp ? (
          <OpenBase64ImageInPopup
            docVerifyPopUp={copyOfApplicantCNICVerifyPopUp}
            hideDocVerifyPopup={() => setCopyOfApplicantCNICVerifyPopUp(false)}
            documentName="Applicant CNIC"
            documentImage={dcoDocuments.applicantCNICFront}
            backDocumentImage={dcoDocuments.applicantCNICBack}
            width="100%"
            document={dcoDocuments.applicantCNICFront}
            documentFlag={dcoDocuments.applicantCNICFrontFlag}
            applicationProgressId={dialogRowData.applicationProgressId}
            objectionStatus={dialogRowData.objectionStatus}
            updateDocFlag={updateDocFlag}
            documentRemarks={
              Number(flag_applicantCNICFront) === 1 || Number(flag_applicantCNICFront) === -1
                ? (applicantCNICBackRemarks || applicantCNICFrontRemarks)
                : ""
            }
            titleToUpdateFlag="copyOfApplicantCNIC"
            flag_document={flag_applicantCNICFront}
            flag_documentBack={flag_applicantCNICBack}
            verificationPermissionService="Disconnection"
            forceVerificationActions={canReviewDocuments}
          />
        ) : null}
      </DcoDocCard>
      <DcoDocCard
        title="Paid Bill Image."
        front={dcoDocuments.paidBillImage}
        flag={flag_paidBillImage ?? dcoDocuments.paidBillImageFlag}
        remarks={paidBillImageRemarks || dcoDocuments.paidBillImageRemarks}
        onOpen={() => {
          if (!paidBillImagePopUp) setPaidBillImagePopUp(true);
        }}
      >
        {paidBillImagePopUp ? (
          <OpenBase64ImageInPopup
            docVerifyPopUp={paidBillImagePopUp}
            hideDocVerifyPopup={() => setPaidBillImagePopUp(false)}
            documentName="Paid Bill Image"
            documentImage={dcoDocuments.paidBillImage}
            width="100%"
            document={dcoDocuments.paidBillImage}
            documentFlag={dcoDocuments.paidBillImageFlag}
            applicationProgressId={dialogRowData.applicationProgressId}
            objectionStatus={dialogRowData.objectionStatus}
            updateDocFlag={updateDocFlag}
            documentRemarks={
              Number(flag_paidBillImage) === 1 || Number(flag_paidBillImage) === -1
                ? paidBillImageRemarks
                : ""
            }
            titleToUpdateFlag="paidBillImage"
            flag_document={flag_paidBillImage}
            verificationPermissionService="Disconnection"
            forceVerificationActions={canReviewDocuments}
          />
        ) : null}
      </DcoDocCard>
      <DcoDocCard
        title="Disconnection Notice."
        front={dcoDocuments.dcoNoticeDoc || dcoDocuments.dcoNoticeImage}
        flag={flag_dcoNotice ?? dcoDocuments.dcoNoticeDocFlag ?? dcoDocuments.dcoNoticeImageFlag}
        remarks={dcoNoticeRemarks || dcoDocuments.dcoNoticeDocRemarks || dcoDocuments.dcoNoticeImageRemarks}
        onOpen={() => {
          if (!dcoNoticePopUp) setDcoNoticePopUp(true);
        }}
      >
        {dcoNoticePopUp ? (
          <OpenBase64ImageInPopup
            docVerifyPopUp={dcoNoticePopUp}
            hideDocVerifyPopup={() => setDcoNoticePopUp(false)}
            documentName="Disconnection Notice"
            documentImage={dcoDocuments.dcoNoticeDoc || dcoDocuments.dcoNoticeImage}
            width="100%"
            document={dcoDocuments.dcoNoticeDoc || dcoDocuments.dcoNoticeImage}
            documentFlag={dcoDocuments.dcoNoticeDocFlag ?? dcoDocuments.dcoNoticeImageFlag}
            applicationProgressId={dialogRowData.applicationProgressId}
            objectionStatus={dialogRowData.objectionStatus}
            updateDocFlag={updateDocFlag}
            documentRemarks={
              Number(flag_dcoNotice) === 1 || Number(flag_dcoNotice) === -1
                ? dcoNoticeRemarks
                : ""
            }
            titleToUpdateFlag="dcoNotice"
            flag_document={flag_dcoNotice}
            verificationPermissionService="Disconnection"
            forceVerificationActions={canReviewDocuments}
          />
        ) : null}
      </DcoDocCard>
    </Grid>
  );

  const lastPaidBillImage = dcoDocuments.paidBillImage || dialogRowData.paidBillImage || "";
  const arrearPaymentDate =
    dialogRowData.paymentDate
    || dialogRowData.paidBillDate
    || dialogRowData.lastPaymentDate
    || dialogRowData.lastPaidDate
    || "";

  const arrearPanel = (
    <>
      <Grid container className="step-title">
        <h4>Arrear Verification</h4>
      </Grid>
      <Grid container className="field-survey-container" spacing={3}>
        {!paymentStatus ? (
          <>
            <Grid item lg={12} sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <Typography sx={{ fontSize: "20px", color: "#666", marginBottom: "10px" }}>
                Click the button below to check arrears.
              </Typography>
            </Grid>
            <Grid item lg={12} sx={{ display: "flex", justifyContent: "center" }}>
              <button
                type="button"
                onClick={() => checkDcoArrears(dialogRowData)}
                className="view-button"
                style={{
                  backgroundColor: "#003971",
                  color: "white",
                  border: "none",
                  borderRadius: "20px",
                  padding: "10px 40px",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              >
                Check Arrears
              </button>
            </Grid>
          </>
        ) : (
          <Grid item lg={12} sx={{ textAlign: "center", marginTop: "20px" }}>
            <Typography
              sx={{
                fontWeight: "bold",
                color: paymentStatus === "No arrears exist" ? "green" : "red",
                fontSize: "28px",
              }}
            >
              {paymentStatus}
            </Typography>
          </Grid>
        )}
      </Grid>
      {paymentStatus && paymentStatus !== "No arrears exist" ? (
        <>
          <Grid container className="dn-payment-submission-container rco-arrear-top-row">
            <Grid item lg={4} className="item">
              <Typography className="title">Arrear Amount</Typography>
              <Typography className="value">
                {numberFormatting(arrearAmount === "" || arrearAmount == null ? 0 : arrearAmount)}
              </Typography>
            </Grid>
            <Grid item lg={4} className="item">
              <Typography className="title">Payment Date</Typography>
              <Typography className="value">
                {arrearPaymentDate ? convertDate(arrearPaymentDate) : "-"}
              </Typography>
            </Grid>
            <Grid item lg={4} className="item">
              <Typography className="title">Last Paid Bill</Typography>
              {lastPaidBillImage ? (
                <div
                  role="button"
                  tabIndex={0}
                  title="Click to view"
                  onClick={() => setPaidBillImagePopUp(true)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setPaidBillImagePopUp(true);
                    }
                  }}
                  style={{ cursor: "pointer", display: "inline-flex" }}
                >
                  {isPdf(lastPaidBillImage) || String(lastPaidBillImage).startsWith("data:application/pdf") ? (
                    <Base64Image data={pdfIcon} width="40" />
                  ) : isImageData(lastPaidBillImage) ? (
                    <Base64Image data={lastPaidBillImage} width="80" height="50" />
                  ) : (
                    <Base64Image data={pdfIcon} width="40" />
                  )}
                </div>
              ) : (
                <Typography className="value">-</Typography>
              )}
            </Grid>
          </Grid>
          {paidBillImagePopUp ? (
            <OpenBase64ImageInPopup
              docVerifyPopUp={paidBillImagePopUp}
              hideDocVerifyPopup={() => setPaidBillImagePopUp(false)}
              documentName="Last Paid Bill"
              documentImage={lastPaidBillImage}
              width="100%"
              document={lastPaidBillImage}
              documentFlag={dcoDocuments.paidBillImageFlag}
              applicationProgressId={dialogRowData.applicationProgressId}
              objectionStatus={dialogRowData.objectionStatus}
              updateDocFlag={() => { }}
              documentRemarks=""
              titleToUpdateFlag="paidBillImage"
              flag_document={flag_paidBillImage}
              verificationPermissionService="Disconnection"
              forceVerificationActions={false}
            />
          ) : null}
        </>
      ) : null}
    </>
  );

  const removeMeterReadingsPanel = (
    <>
      <h3 className="step-title">Remove Meter Readings</h3>
      <DcoRemovedMeterReadingTable
        ref={removedMeterTableRef}
        key={`${dialogRowData.applicationId || "dco-removed-meter"}-${removedMeterUnits.length}`}
        units={removedMeterUnits}
        tariffCode={dialogRowData.tariffCode || dialogRowData.tariffCd || dialogRowData.tariffDesc || ""}
        onValidationError={setError}
      />
    </>
  );

  const authorityDecisionPanel = (
    <>
      <h3 className="step-title">Authority Decision</h3>
      <Grid container className="authority-verification-container dco-authority-decision">
        <Grid item xs={12} md={6} className="authority-radio-col authority-radio-col--survey">
          <Typography className="fieldLabel">Is Refer to M&T?</Typography>
          <FormControl className="authority-radio-panel">
            <RadioGroup
              row
              className="authority-radio-group"
              value={referToFieldSurvey}
              onChange={(event) => setReferToFieldSurvey(event.target.value)}
            >
              <FormControlLabel
                value="yes"
                className="authority-radio-option"
                control={<Radio />}
                label="Yes"
              />
              <FormControlLabel
                value="no"
                className="authority-radio-option"
                control={<Radio />}
                label="No"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          {authorityMeterUnits.length ? (
            <DcoRemovedMeterReadingTable
              key={`${dialogRowData.applicationId || "dco-authority-meter"}-${authorityMeterUnits.length}`}
              units={authorityMeterUnits}
              tariffCode={dialogRowData.tariffCode || dialogRowData.tariffCd || dialogRowData.tariffDesc || ""}
              readOnly
            />
          ) : (
            <div className="description-box">
              <p className="group-description">
                <InfoIcon className="icon" />
                Removed meter readings not found for this application.
              </p>
            </div>
          )}
        </Grid>
      </Grid>
    </>
  );

  return (
    <>
      <ServiceDashboardTables
        enabled={showDashboard}
        comfortableRows
        isLoadingNormal={Boolean(isLoad)}
        isLoadingOverdue={Boolean(isLoadd)}
        normalTitle={`Disconnection ( ${numberFormatting(totalRecordsNormal) ? numberFormatting(totalRecordsNormal) : "-"} )`}
        overdueCount={numberFormatting(totalRecordsOverdue) ? numberFormatting(totalRecordsOverdue) : "-"}
        normalColumns={tblColumns}
        overdueColumns={tblColumnsOverDue}
        normalData={applicationsNormalToDataGrid}
        overdueData={applicationsOverdueToDataGrid}
        onRowClick={handleRowClick}
        mobileFields={DC_MOBILE_FIELDS}
        showCompanyFilter={officeCode == 10}
        companies={DISCO_COMPANIES}
        selectedOfficeCode={selectedOfficeCode}
        onOfficeCodeChange={setSelectedOfficeCode}
        normalTableProps={{
          firstPage: handleFirstPage,
          lastPage: handleLastPage,
          totalPages: totalPagesNormal,
          totalRecords: totalRecordsNormal,
          currentPage: currentPageNormal,
          handleFilterDateFrom: handleDateFromFilter,
          handleFilterDateTo: handleDateToFilter,
          resetDateFilter: handleResetDateFilter,
          handleFilterProgressId: handleProgressIdFilter,
          handleFilterObjectionStatus: handleObjectionStatusFilter,
          resetProgressIdFilter: handleResetProgressIdFilter,
          resetObjectionStatusFilter: handleResetObjectionStatusFilter,
          nextPage: handleNextPage,
          previousPage: handlePreviousPage,
        }}
        overdueTableProps={{
          firstPage: handleFirstPageOverdue,
          nextPage: handleNextPageOverdue,
          previousPage: handlePreviousPageOverdue,
          lastPage: handleLastPageOverdue,
          handleFilterDateFrom: handleDateFromFilterOverdue,
          handleFilterDateTo: handleDateToFilterOverdue,
          resetDateFilter: handleResetDateFilterOverdue,
          resetProgressIdFilter: handleResetProgressIdFilterOverdue,
          resetObjectionStatusFilter: handleResetObjectionStatusFilterOverdue,
          handleFilterProgressId: handleProgressIdFilterOverdue,
          handleFilterObjectionStatus: handleObjectionStatusFilterOverdue,
          totalPages: totalPagesOverdue,
          totalRecords: totalRecordsOverdue,
          currentPage: currentPageOverdue,
        }}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xl"
        {...workflowDialogShellProps(isWorkflowMobile)}
        className={workflowDialogClassName("main-dialog rco-workflow-dialog rco-workflow-dialog--reconnection dco-workflow-dialog", isWorkflowMobile)}
        PaperProps={{ className: "rco-workflow-dialog-paper" }}
      >
        <DialogTitle className="dialog-title">
          <div className="rco-dialog-header">
            <Typography className="applicationTitleText">
              Disconnection -
              <span style={{ color: "white" }}> {statusLabel(dialogRowData)}</span>
            </Typography>
            <button type="button" className="closeIcon" onClick={handleClose} aria-label="Close dialog">
              <CloseIcon id="clearPopup" sx={{ color: "white" }} />
            </button>
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div" className="dialog-content">
            <Grid container className="headercontainer">
              <Grid item lg={12} className="status-bar">
                <StepProgressBar
                  array={DCO_STATUS_BAR}
                  applicationProgressId={Number.isFinite(progressId) ? progressId : 0}
                  objection={objectionStatus}
                  openDetailsPopUp={() => { }}
                  pageName="Disconnection"
                />
              </Grid>
              <Grid item xs={12} className="application-details">
                <div className="rco-application-details-body">
                  <div className="rco-details-grid">
                    <DcoDetailItem title="Application Id" value={dialogRowData.applicationId} />
                    <DcoDetailItem title="Applicant Name" value={dialogRowData.applicantName || dialogRowData.consumerName} />
                    <DcoDetailItem title="Applicant CNIC" value={dialogRowData.consumerCNIC} />
                    <DcoDetailItem title="Father Name" value={dialogRowData.consumerFatherName} />
                    <DcoDetailItem title="Sanct. Load(kW)" value={dialogRowData.sanctionedLoad} />
                    <DcoDetailItem title="Connection Type" value={dialogRowData.connectionCategory} />
                    <DcoDetailItem title="Meter Type" value={dialogRowData.meterType} />
                    <DcoDetailItem title="Tariff" value={dialogRowData.tariffDesc || dialogRowData.tariffCode} />
                    <DcoDetailItem title="Subdivision" value={subdivisionName(dialogRowData.subDivisionCode, subDivisions)} />
                    <DcoDetailItem title="Ref. No." value={dialogRowData.referenceNo || dialogRowData.refNo} />
                    <DcoDetailItem title="Consumer Id." value={dialogRowData.customerId} />
                    <DcoDetailItem title="Phone No." value={dialogRowData.applicantMobileNo} />
                    <DcoDetailItem title="Address" value={dialogRowData.consumerAddress} />
                    <DcoDetailItem title="Type" value={disconnectionTypeLabel(dialogRowData)} />
                    <DcoDetailItem title="Consumer Status" value={dialogRowData.consumerStatus} />
                    <DcoDetailItem title="Area Type" value={areaTypeLabel} />
                    <DcoDetailItem title="Unpaid Debt" value={dialogRowData.unPaidDebt} />
                    <DcoDetailItem title="Arrears" value={dialogRowData.arrearsAmount} />
                    <DcoDetailItem title="Arrear Age" value={dialogRowData.arrearsAge} />
                    <DcoDetailItem title="Bill Month" value={convertDate(dialogRowData.billMonth)} />
                    <DcoDetailItem title="Due Date" value={convertDate(dialogRowData.dueDate)} />
                    <DcoDetailItem title="Notice Date" value={convertDate(dialogRowData.dcoNoticeDate)} />
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} className="rco-workflow-panels-wrap">
                <div
                  className={`rco-workflow-panels${activityLogCollapsed ? " rco-workflow-panels--activity-collapsed" : ""
                    }${progressId === 3 || progressId === 4 ? " rco-workflow-panels--dco-meter" : ""}`}
                >
                  <div
                    className={`processing-summary${activityLogCollapsed ? " processing-summary--collapsed" : ""
                      }`}
                  >
                    <div className="rco-activity-log-head">
                      <button
                        type="button"
                        className="rco-activity-collapse-btn"
                        onClick={() => setActivityLogCollapsed((prev) => !prev)}
                        aria-expanded={!activityLogCollapsed}
                        aria-label={activityLogCollapsed ? "Expand Activity Log" : "Collapse Activity Log"}
                        title={activityLogCollapsed ? "Expand Activity Log" : "Collapse Activity Log"}
                      >
                        {activityLogCollapsed ? (
                          <ChevronRightIcon fontSize="small" />
                        ) : (
                          <ChevronLeftIcon fontSize="small" />
                        )}
                      </button>
                      <p className="title">Activity Log</p>
                      {!activityLogCollapsed && dcoHistoryItems.length > 0 ? (
                        <button
                          type="button"
                          className="rco-activity-view-detail-btn"
                          onClick={() => setActivityLogDetailOpen(true)}
                        >
                          <VisibilityOutlinedIcon />
                          Timeline
                        </button>
                      ) : null}
                    </div>
                    {!activityLogCollapsed ? (
                      <div className="rco-activity-log-body">
                        {activityLoading ? (
                          <div className="rco-load-activity-wrap rco-load-activity-wrap--loading">
                            <AppLoader size={30} />
                          </div>
                        ) : activityLog.length > 0 ? (
                          <ProcessingSummaryTableForCoa
                            data={activityLog}
                            columns={DCO_ACTIVITY_COLUMNS}
                            openDetailsPopUp={(fullRow) => {
                              openActivityLogDetails(fullRow._raw || fullRow);
                            }}
                          />
                        ) : (
                          <div className="rco-load-activity-wrap">
                            {activityLoaded ? (
                              <p className="group-description">No activity log found.</p>
                            ) : (
                              <button
                                type="button"
                                className="rco-load-activity-btn"
                                onClick={loadActivityLog}
                              >
                                Load Activity Log
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                  <div className="action-form">
                    <div className="fields">
                      {progressId === 0 ? (
                        <>
                          <h3 className="step-title">Document Submission</h3>
                          <div className="description-box description-box--error">
                            <p className="group-description">
                              <InfoIcon className="icon" />
                              To process application, some documents are yet to be provided by the customer.
                            </p>
                          </div>
                          {hasDocuments ? documentsPanel : null}
                        </>
                      ) : progressId === 1 && objectionStatus === 1 ? (
                        <>
                          <h3 className="step-title">Documents Objection</h3>
                          <div className="description-box description-box--error">
                            <p className="group-description">
                              <InfoIcon className="icon" />
                              Documents are objected. Reupload the required documents to continue.
                            </p>
                          </div>
                          {documentsPanel}
                        </>
                      ) : progressId === 1 ? (
                        <>
                          <h3 className="step-title">Document Verification</h3>
                          {hasDocuments ? documentsPanel : (
                            <div className="description-box">
                              <p className="group-description">
                                <InfoIcon className="icon" />
                                No documents found on this application.
                              </p>
                            </div>
                          )}
                        </>
                      ) : progressId === 2 ? (
                        arrearPanel
                      ) : progressId === 3 ? (
                        removeMeterReadingsPanel
                      ) : progressId === 4 ? (
                        authorityDecisionPanel
                      ) : (
                        <>
                          <h3 className="step-title">{statusLabel(dialogRowData)}</h3>
                          <div className="description-box">
                            <p className="group-description">
                              <InfoIcon className="icon" />
                              {progressId === 8
                                ? "Application is completed."
                                : "This step is in process."}
                            </p>
                          </div>
                          {hasDocuments ? documentsPanel : null}
                        </>
                      )}
                    </div>
                    {canReviewDocuments && hasDocuments ? (
                      <div className="actions dco-workflow-footer">
                        <button
                          type="button"
                          className="view-button"
                          disabled={!canSubmitDcoDocumentVerification}
                          title={
                            !canSubmitDcoDocumentVerification
                              ? "Accept or reject each document before verifying."
                              : undefined
                          }
                          onClick={updateDocumentStatus}
                          style={{
                            backgroundColor: hasRejectedDcoDocument ? "#E11B3C" : "#003971",
                          }}
                        >
                          {hasRejectedDcoDocument ? "Reject" : "Verify"}
                        </button>
                      </div>
                    ) : progressId === 1 && objectionStatus === 1 ? (
                      <div className="actions dco-workflow-footer">
                        <button
                          type="button"
                          className="view-button"
                          disabled={!hasRejectedDocsToReupload}
                          title={
                            !hasRejectedDocsToReupload
                              ? "Documents re-uploaded. Pending verification."
                              : undefined
                          }
                          onClick={() => {
                            if (!hasRejectedDocsToReupload) return;
                            setOpen(false);
                            if (!props.showTable && typeof props.onCloseSearchResults === "function") {
                              props.onCloseSearchResults();
                            }
                            navigate("/disconnection-update", {
                              state: {
                                dialogRowData: { ...dialogRowData, ...dcoDocuments },
                              },
                            });
                          }}
                        >
                          Re-Upload Document
                        </button>
                      </div>
                    ) : canReviewArrears && paymentStatus ? (
                      <div className="actions dco-workflow-footer">
                        <button
                          type="button"
                          className="view-button"
                          onClick={() =>
                            callArrearVerificationAPI(
                              dialogRowData.applicationId,
                              progressId,
                              1,
                              "Arrears Rejected.",
                              "Arrears Rejected.",
                            )
                          }
                          style={{
                            backgroundColor: "#e11b3c",
                            color: "white",
                            border: "none",
                            borderRadius: "20px",
                            padding: "10px 40px",
                            fontSize: "16px",
                            cursor: "pointer",
                          }}
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          className="view-button"
                          onClick={() =>
                            callArrearVerificationAPI(
                              dialogRowData.applicationId,
                              progressId + 1,
                              0,
                              "Arrears Verified.",
                              "Arrears Verified.",
                            )
                          }
                          style={{
                            backgroundColor: "#003971",
                            color: "white",
                            border: "none",
                            borderRadius: "20px",
                            padding: "10px 40px",
                            fontSize: "16px",
                            cursor: "pointer",
                          }}
                        >
                          Approve
                        </button>
                      </div>
                    ) : canReviewRemovedMeter ? (
                      <div className="actions dco-workflow-footer">
                        <button
                          type="button"
                          className="view-button"
                          onClick={submitRemovedMeterReadings}
                          style={{
                            backgroundColor: "#003971",
                            color: "white",
                            border: "none",
                            borderRadius: "20px",
                            padding: "10px 40px",
                            fontSize: "16px",
                            cursor: "pointer",
                          }}
                        >
                          Submit
                        </button>
                      </div>
                    ) : canReviewAuthorityDecision ? (
                      <div className="actions dco-workflow-footer">
                        <button
                          type="button"
                          className="view-button"
                          disabled={!referToFieldSurvey}
                          title={
                            !referToFieldSurvey
                              ? "Select Yes or No for M&T Verification."
                              : undefined
                          }
                          style={{
                            backgroundColor: "#003971",
                            color: "white",
                            border: "none",
                            borderRadius: "20px",
                            padding: "10px 40px",
                            fontSize: "16px",
                            cursor: referToFieldSurvey ? "pointer" : "not-allowed",
                            opacity: referToFieldSurvey ? 1 : 0.55,
                          }}
                        >
                          {referToFieldSurvey === "yes"
                            ? "Refer to M&T"
                            : "Submit"}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
      </Dialog>

      <RcoActivityLogDialog
        open={historyDetailPopUp}
        onClose={() => {
          setHistoryDetailPopUp(false);
          setHistoryData(null);
        }}
        title={historyData?.dcoProgStatusName || "Activity Details"}
        variant={Number(historyData?.objectionStatus) === 1 ? "action" : "submitted"}
        footer={
          historyData
            ? `Processed by: ${historyData?.lastModifiedBy || "-"} on: ${historyData?.lastModificationDate
              ? convertDatewithTime(historyData.lastModificationDate)
              : "-"
            }`
            : null
        }
      >
        <div className="wf-application-details-body">
          {historyData ? (
            <>
              <div className="rco-history-details-grid rco-history-details-grid--2col">
                <RcoHistoryField
                  label="Step"
                  value={historyData.dcoProgStatusName || "-"}
                />
                <RcoHistoryField
                  label="Status"
                  value={
                    Number(historyData.objectionStatus) === 1
                      ? "Objected / Rejected"
                      : Number(historyData.objectionStatus) === 2
                        ? "Resolved"
                        : "Processed"
                  }
                />
                <RcoHistoryField
                  label="Remarks"
                  value={historyData.remarks || "N/A"}
                />
                <RcoHistoryField
                  label="Remarks Detail"
                  value={historyData.remarksDetail || "N/A"}
                />
              </div>
              {(historyData.applicantCNICFront || historyData.applicantCNICBack || historyData.paidBillImage || historyData.dcoNoticeDoc || historyData.dcoNoticeImage) ? (
                <div className="rco-history-docs">
                  {historyData.applicantCNICFront ? (
                    <RcoHistoryDoc src={historyData.applicantCNICFront} label="CNIC Front" />
                  ) : null}
                  {historyData.applicantCNICBack ? (
                    <RcoHistoryDoc src={historyData.applicantCNICBack} label="CNIC Back" />
                  ) : null}
                  {historyData.paidBillImage ? (
                    <RcoHistoryDoc src={historyData.paidBillImage} label="Paid Bill" />
                  ) : null}
                  {(historyData.dcoNoticeDoc || historyData.dcoNoticeImage) ? (
                    <RcoHistoryDoc src={historyData.dcoNoticeDoc || historyData.dcoNoticeImage} label="Disconnection Notice" />
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </RcoActivityLogDialog>

      <RcoActivityLogDetailDialog
        open={activityLogDetailOpen}
        onClose={() => setActivityLogDetailOpen(false)}
        historyItems={dcoHistoryItems}
        currentStatus={dcoHistoryCurrent}
        currentProgressId={dialogRowData?.applicationProgressId}
        buildDetailRows={buildDcoActivityLogDetailRows}
        applicationDetails={{
          applicationId: dialogRowData?.applicationId || "",
          name: dialogRowData?.applicantName || dialogRowData?.consumerName || "",
          sanctionedLoad: dialogRowData?.sanctionedLoad ?? "",
          tariff: dialogRowData?.tariffDesc || dialogRowData?.tariffCode || "",
          companyCode: dialogRowData?.companyCode || dialogRowData?.discoCode || "",
        }}
        formatDateFn={convertDatewithTime}
        onStepClick={openActivityLogDetails}
        timelineType={ACTIVITY_LOG_TIMELINE_TYPES.DISCONNECTION}
      />

      {Boolean(error) ? (
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={8000}
          onClose={(_, reason) => {
            if (reason === "clickaway") return;
            setError("");
          }}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        >
          <div className="mrc-validation-toast" role="alert" aria-live="assertive">
            <div className="mrc-validation-toast__icon-wrap" aria-hidden="true">
              <WarningRoundedIcon className="mrc-validation-toast__icon" />
            </div>
            <span className="mrc-validation-toast__divider" aria-hidden="true" />
            <div className="mrc-validation-toast__body">
              <p className="mrc-validation-toast__eyebrow">Error</p>
              <p className="mrc-validation-toast__message">{error}</p>
            </div>
            <IconButton
              size="small"
              aria-label="Dismiss validation message"
              className="mrc-validation-toast__close"
              onClick={() => setError("")}
              sx={{ position: "absolute", top: 6, right: 6 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </Snackbar>
      ) : null}

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={viewBackdrop}
      >
        <AppLoader />
      </Backdrop>
    </>
  );
});

DisconnectionTable.displayName = 'DisconnectionTable';

export default DisconnectionTable;
